import { useState, useCallback } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { IdCard, Mail, UserPlusIcon } from 'lucide-react';
import { useSnapshot } from 'valtio';
import { skipToken } from '@tanstack/react-query';
import {
  CourseReferralModalMode,
  courseReferralModalState,
} from '../tenantState/CourseReferralState';
import { CustomStepper } from '@duke-hq/ui/components/StepWizard/CustomStepper';
import { useStep } from '@duke-hq/ui/components/StepWizard/useStep';
import { CourseStatusSchema } from '@duke-hq/svc-api-db/schemas/enums/CourseStatus.schema';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { CourseSupersessionEventTypeSchema } from '@duke-hq/svc-api-db/schemas/enums/CourseSupersessionEventType.schema';
import { EnrolmentSettings } from './CourseReferralStepForms/EnrolmentSettings';
import { ReviewCourse } from './CourseReferralStepForms/ReviewCourse';
import { SelectCourse } from './CourseReferralStepForms/SelectCourse';
import { Summary } from './CourseReferralStepForms/Summary';
import { FormModalWrapper } from '@duke-hq/ui/components/Modals/FormModalWrapper';
import { ReferralActivationInfo } from './ReferralActivationInfo';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';

import { testIds } from './CourseReferralModal.testIds';

const STEPS = [
  {
    title: () => t`Select Course`,
    key: 'SelectCourse',
    component: SelectCourse,
  },
  {
    title: () => t`Review Course`,
    key: 'ReviewCourse',
    component: ReviewCourse,
  },
  {
    title: () => t`Settings`,
    key: 'EnrolmentSettings',
    component: EnrolmentSettings,
  },
  {
    title: () => t`Summary`,
    key: 'Summary',
    component: Summary,
  },
];

function StudentDetailsBanner(studentMetadata: {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  usi?: string;
}) {
  return (
    <Box w="full" px="24px">
      <Flex
        w="full"
        h="full"
        px="12px"
        py="8px"
        bg="bg.muted"
        color="fg.muted"
        alignItems={{ base: 'start', md: 'center' }}
        gap="10px"
        borderRadius="md"
        direction={{ base: 'column', md: 'row' }}
      >
        <HStack bg="bg.muted" color="fg.muted" gap="4px" align="center" isTruncated>
          <Text fontSize="md" fontWeight="semibold">
            <Trans>Referring</Trans>
          </Text>
          <Text fontSize="md" fontWeight="medium">
            {studentMetadata?.firstName} {studentMetadata?.lastName}
          </Text>
        </HStack>
        {studentMetadata?.usi !== null ? (
          <HStack bg="bg.muted" color="fg.muted" gap="4px" align="center" isTruncated>
            <Icon as={IdCard} height="20px" width="20px" />
            <Text fontSize="sm" fontWeight="medium">
              {studentMetadata?.usi}
            </Text>
          </HStack>
        ) : null}
        <HStack bg="bg.muted" color="fg.muted" gap="4px" align="center" isTruncated>
          <Icon as={Mail} height="20px" width="20px" />
          <Text fontSize="sm" fontWeight="medium">
            {studentMetadata?.email}
          </Text>
        </HStack>
      </Flex>
    </Box>
  );
}

function SupersessionWarningBanner() {
  const modalState = useSnapshot(courseReferralModalState);

  const currentCourseIdentifier = modalState.selectedCourseAttributes.identifier;
  const currentCourseTitle = modalState.selectedCourseAttributes.title;
  const status = modalState.selectedCourseAttributes.status;
  const courseId = modalState.selectedCourseAttributes.id;
  const isSuperseded = status === CourseStatusSchema.enum.SUPERSEDED;

  // Query supersession history when course is superseded
  const { data: supersessionData } = trpcTenant.courses.courseSupersessionHistory.useQuery(
    courseId && isSuperseded ? { courseId } : skipToken,
  );

  if (courseId == null || isSuperseded == null) {
    return null;
  }

  // Only show if supersession was toggled on
  if (
    supersessionData != null &&
    supersessionData.eventType !== CourseSupersessionEventTypeSchema.enum.SUPERSEDED_TOGGLED_ON
  ) {
    return null;
  }

  if (supersessionData == null) {
    return null;
  }

  const supersededByCourse = supersessionData.supersededByCourse;

  const currentCourseDisplay =
    currentCourseIdentifier && currentCourseTitle
      ? `${currentCourseIdentifier} ${currentCourseTitle}`
      : null;

  const supersededCourseDisplay =
    supersededByCourse &&
    supersededByCourse.identifier !== null &&
    supersededByCourse.title !== null
      ? `${supersededByCourse.identifier} ${supersededByCourse.title}`
      : supersededByCourse
        ? supersededByCourse.title
        : null;

  return (
    <Box w="full" px="24px">
      <Alert status="warning" borderRadius="md">
        <AlertIcon alignSelf="flex-start" mt="2px" />
        <AlertDescription>
          <VStack align="start" spacing="0px" gap="0px">
            <Text fontSize="sm" fontWeight="bold" lineHeight="1.5">
              {currentCourseDisplay !== null ? (
                <>
                  {currentCourseDisplay} <Trans>has been superseded</Trans>
                </>
              ) : (
                <Trans>This course has been superseded</Trans>
              )}
            </Text>
            <Text fontSize="sm" lineHeight="1.5">
              {supersededCourseDisplay !== null ? (
                <>
                  <Trans>A transfer to</Trans> {supersededCourseDisplay}{' '}
                  <Trans>will be processed by Duke Support once the ticket is created.</Trans>
                </>
              ) : (
                <Trans>
                  A transfer to the new course will be processed by Duke Support once the ticket is
                  created.
                </Trans>
              )}
            </Text>
          </VStack>
        </AlertDescription>
      </Alert>
    </Box>
  );
}

function ModalContent({
  isReferralCreationSuccess,
  activationInfo,
  onReferralCreationSuccess,
  mode,
  studentMetadata,
}: {
  isReferralCreationSuccess: boolean;
  activationInfo: RouterOutputs['courseReferral']['create'] | null;
  onReferralCreationSuccess: (activationInfo: RouterOutputs['courseReferral']['create']) => void;
  mode: CourseReferralModalMode;
  studentMetadata?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    usi?: string;
  };
}) {
  const { currentStep, setStep, goToNextStep, goToPrevStep } = useStep({
    maxStep: STEPS.length - 1,
    initialStep:
      mode === CourseReferralModalMode.ADD_SECOND_SEMESTER ||
      mode === CourseReferralModalMode.CREATE_NEW_FOR_SELECTED_COURSE
        ? 1
        : 0,
  });

  if (isReferralCreationSuccess && activationInfo) {
    return <ReferralActivationInfo activationInfo={activationInfo} />;
  }

  return (
    <VStack w="full" h="full" justify="start" gap="16px" data-testid={testIds.modal}>
      <Box w="full">
        <Stack spacing="0" direction={{ base: 'column', md: 'row' }}>
          {STEPS.map((step, id) => (
            <CustomStepper
              key={step.key}
              cursor="pointer"
              onClick={() => {
                if (currentStep > id) {
                  if (id === 0 && mode === CourseReferralModalMode.ADD_SECOND_SEMESTER) {
                    return;
                  }
                  setStep(id);
                }
              }}
              title={step.title()}
              isActive={currentStep === id}
              isCompleted={currentStep > id}
              isFirstStep={id === 0}
              isLastStep={STEPS.length === id + 1}
            />
          ))}
        </Stack>
      </Box>
      {mode === CourseReferralModalMode.ADD_SECOND_SEMESTER && studentMetadata ? (
        <>
          <StudentDetailsBanner {...studentMetadata} />
          <SupersessionWarningBanner />
        </>
      ) : null}
      <Box w="full" h="full">
        <CourseReferralSteps
          currentStep={currentStep}
          goToNextStep={goToNextStep}
          goToPrevStep={goToPrevStep}
          setStep={setStep}
          onReferralCreationSuccess={onReferralCreationSuccess}
        />
      </Box>
    </VStack>
  );
}

function CourseReferralSteps({
  currentStep,
  goToNextStep,
  goToPrevStep,
  setStep,
  onReferralCreationSuccess,
}: {
  currentStep: number;
  goToNextStep: () => void;
  goToPrevStep: () => void;
  setStep: (step: React.SetStateAction<number>) => void;
  onReferralCreationSuccess: (activationInfo: RouterOutputs['courseReferral']['create']) => void;
}) {
  const step = STEPS[currentStep];
  if (!step) {
    return null;
  }

  const { key, component: Step } = step;
  return (
    <Step
      key={key}
      goToNextStep={goToNextStep}
      goToPrevStep={goToPrevStep}
      setStep={setStep}
      onReferralCreationSuccess={onReferralCreationSuccess}
    />
  );
}

/**
 * The "Create referral" button that opens the course referral modal.
 * Only render this where you want the button visible.
 */
export function CourseReferralButton() {
  const { openModal } = courseReferralModalState;

  return (
    <Button size="sm" leftIcon={<Icon as={UserPlusIcon} />} onClick={openModal}>
      <Trans>Create referral</Trans>
    </Button>
  );
}

/**
 * The course referral modal. Listens to valtio state and renders no visible UI
 * until opened. Mount this once at a high level (e.g. Navbar) so it's always
 * available for `CourseReferralButton` and the "Add semester" action.
 */
export function CourseReferralModal() {
  const courseReferralModalSnap = useSnapshot(courseReferralModalState);
  const { closeModal } = courseReferralModalState;
  const [isReferralCreationSuccess, setIsReferralCreationSuccess] = useState(false);
  const [activationInfo, setActivationInfo] = useState<
    RouterOutputs['courseReferral']['create'] | null
  >(null);

  const handleReferralCreationSuccess = useCallback(
    (activationInfo: RouterOutputs['courseReferral']['create']) => {
      setActivationInfo(activationInfo);
      setIsReferralCreationSuccess(true);
    },
    [setActivationInfo, setIsReferralCreationSuccess],
  );

  // Reset local state here so the next open always gets a fresh form.
  // Valtio's closeModal() handles resetting the shared store (via resetStates).
  const handleClose = useCallback(() => {
    closeModal();
    setIsReferralCreationSuccess(false);
    setActivationInfo(null);
  }, [closeModal, setIsReferralCreationSuccess, setActivationInfo]);

  return (
    <FormModalWrapper
      modalContent={
        <ModalContent
          isReferralCreationSuccess={isReferralCreationSuccess}
          activationInfo={activationInfo}
          onReferralCreationSuccess={handleReferralCreationSuccess}
          mode={courseReferralModalSnap.mode}
          studentMetadata={courseReferralModalSnap.selectedStudent}
        />
      }
      isOpen={courseReferralModalSnap.isModalOpen}
      onClose={handleClose}
      modalContentHeight={isReferralCreationSuccess ? 'auto' : '700px'}
      bgColor={isReferralCreationSuccess ? 'bg.muted' : 'bg.surface'}
    />
  );
}
